import { useCallback, useState } from 'react';

/**
 * The rail collapses to its icons, and remembers that it did.
 *
 * A preference rather than a place, so it goes in `localStorage` and follows the admin into
 * new tabs and later visits, where the rail's scroll position (`./railScroll`) is per tab.
 *
 * With nothing saved, a window narrower than the 3a handoff's 1100px opens collapsed, which
 * is what the handoff asked for. That is only the starting state: it is not saved, so the
 * choice is still the admin's the first time they press the button.
 */
export const RAIL_COLLAPSED_KEY = 'cms.rail.collapsed';
export const NARROW_WINDOW = 1100;

/** `window.localStorage`, or null where reading it throws (blocked storage, a sandbox). */
const localStore = () => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

export const readRailCollapsed = (storage = localStore(), width = globalThis.innerWidth) => {
  try {
    const saved = storage?.getItem(RAIL_COLLAPSED_KEY);
    if (saved === '1') return true;
    if (saved === '0') return false;
  } catch {
    // Blocked storage: fall through to the window's width.
  }
  return typeof width === 'number' && width < NARROW_WINDOW;
};

export const writeRailCollapsed = (collapsed, storage = localStore()) => {
  try {
    storage?.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Full or blocked storage. The rail then opens as it would with nothing saved.
  }
};

/** `[collapsed, toggle]`. The toggle saves the new state as it sets it. */
export const useRailCollapsed = () => {
  // The function, not its result: read once, on the first render.
  const [collapsed, setCollapsed] = useState(readRailCollapsed);
  const toggle = useCallback(() => {
    writeRailCollapsed(!collapsed);
    setCollapsed(!collapsed);
  }, [collapsed]);
  return [collapsed, toggle];
};
